// Tobacco-cessation counselling — pre→post educational-intervention KAP instrument.
// STARTER DRAFTS pending expert validation. No answer keys are stored in this file.
import { baseDemographics, TF, mc, same } from './common';

const demographics = [
  ...baseDemographics,
  {
    key: 'A4',
    field: 'tobaccoUse',
    type: 'single',
    stem: 'Do you currently use any form of tobacco (smoked or smokeless)?',
    options: same('Never used', 'Former user', 'Current user'),
  },
];

const knowledge = [
  {
    key: 'KQ1',
    type: 'single',
    options: TF,
    stem: 'The “5A’s” (Ask, Advise, Assess, Assist, Arrange) are a recognised framework for delivering brief tobacco-cessation intervention.',
  },
  {
    key: 'KQ2',
    type: 'single',
    options: TF,
    stem: 'Even brief advice to quit from a clinician (under three minutes) measurably increases a patient’s likelihood of stopping tobacco use.',
  },
  {
    key: 'KQ3',
    type: 'single',
    options: TF,
    stem: 'The “5R’s” (Relevance, Risks, Rewards, Roadblocks, Repetition) are intended for patients who are currently unwilling to make a quit attempt.',
  },
  {
    key: 'KQ4',
    type: 'single',
    options: mc(
      'Nicotine replacement therapy (patch, gum, lozenge)',
      'Varenicline',
      'Bupropion',
      'All of the above',
    ),
    stem: 'Which of the following is an evidence-based pharmacotherapy for tobacco cessation?',
  },
  {
    key: 'KQ5',
    type: 'single',
    options: mc(
      'Faster wound healing',
      'Reduced risk of gum disease',
      'Periodontal disease and oral cancer',
      'Whiter tooth enamel',
    ),
    stem: 'Which oral effect is associated with tobacco use?',
  },
  {
    key: 'KQ6',
    type: 'single',
    options: TF,
    stem: 'Tobacco use is an established risk factor for periodontal disease and for failure of dental implants.',
  },
  {
    key: 'KQ7',
    type: 'single',
    options: TF,
    stem: 'Smokeless tobacco and areca (betel) nut are major contributors to oral potentially malignant disorders, particularly in the Indian population.',
  },
  {
    key: 'KQ8',
    type: 'single',
    options: TF,
    stem: 'Smokeless (chewing) tobacco is a safe alternative to smoking and carries no risk of oral cancer.',
  },
  {
    key: 'KQ9',
    type: 'single',
    options: mc(
      'The Fagerström Test for Nicotine Dependence',
      'The Glasgow Coma Scale',
      'The DMFT index',
      'The Mallampati score',
    ),
    stem: 'Which tool is commonly used to assess a patient’s level of nicotine dependence?',
  },
  {
    key: 'KQ10',
    type: 'single',
    options: TF,
    stem: 'Tobacco use is associated with delayed wound healing after oral surgical and periodontal procedures.',
  },
  {
    key: 'KQ11',
    type: 'single',
    options: TF,
    stem: 'A telephone “quitline” is an appropriate resource to which a dentist can refer a patient who wants help to quit tobacco.',
  },
  {
    key: 'KQ12',
    type: 'single',
    options: TF,
    stem: 'Tobacco-use status only needs to be recorded for patients who present with visible oral lesions.',
  },
];

// Attitude — 8 items. Reverse-coded (negatively-worded): AQ3, AQ4, AQ5 (see keys.ts).
// The "I feel confident…" items moved to the self-efficacy subscale below (v2.0).
const attitude = [
  {
    key: 'AQ1',
    type: 'likert',
    stem: 'Providing tobacco-cessation counselling is part of a dentist’s professional responsibility.',
  },
  {
    key: 'AQ2',
    type: 'likert',
    stem: 'Tobacco-cessation counselling delivered in the dental clinic is effective in helping patients quit.',
  },
  {
    key: 'AQ3',
    type: 'likert',
    stem: 'Asking patients about their tobacco use is likely to offend them and harm the clinician–patient relationship.',
  },
  {
    key: 'AQ4',
    type: 'likert',
    stem: 'There is not enough time during a routine dental appointment to counsel patients about quitting tobacco.',
  },
  {
    key: 'AQ5',
    type: 'likert',
    stem: 'Counselling patients to quit tobacco is not worthwhile because they rarely succeed in stopping.',
  },
  {
    key: 'AQ6',
    type: 'likert',
    stem: 'Recording every patient’s tobacco-use status should be a routine part of the dental record.',
  },
  {
    key: 'AQ7',
    type: 'likert',
    stem: 'Dentists should recommend pharmacotherapy (e.g. nicotine replacement therapy) or refer patients to a quitline when appropriate.',
  },
  {
    key: 'AQ8',
    type: 'likert',
    stem: 'I would like more training in tobacco-cessation counselling.',
  },
];

// Self-efficacy / confidence — 5 items mapped to the 5 A’s. Summed (max 25), no
// reverse-coding; reported as a separate construct from attitude (v2.0).
const selfEfficacy = [
  {
    key: 'SE1',
    type: 'likert',
    stem: 'I feel confident in my ability to advise patients on how to quit tobacco.',
  },
  {
    key: 'SE2',
    type: 'likert',
    stem: 'I have adequate knowledge and training to deliver effective tobacco-cessation counselling.',
  },
  {
    key: 'SE3',
    type: 'likert',
    stem: 'I feel confident asking every patient about their tobacco use, including smokeless tobacco and areca (betel) nut.',
  },
  {
    key: 'SE4',
    type: 'likert',
    stem: 'I feel confident assessing a patient’s readiness to quit and using a motivational approach (the 5 R’s) for those who are not yet ready.',
  },
  {
    key: 'SE5',
    type: 'likert',
    stem: 'I feel confident assisting a patient to quit — discussing pharmacotherapy (e.g. NRT) and arranging follow-up or a quitline referral.',
  },
];

// Practice section, in three parts (v2.0):
//  • PQ1–PQ7 — self-reported frequency (descriptive, NOT scored).
//  • V1–V3   — applied-knowledge vignettes, one guideline-appropriate answer each,
//              scored via the practice key (max 3); answerable at any training stage.
//  • IN1–IN2 — behavioural intention (descriptive Likert), meaningful for pre-clinical
//              respondents for whom current practice does not yet apply.
const practice = [
  {
    key: 'PQ1',
    type: 'single',
    stem: 'How often do you ask and record patients’ tobacco-use status in their notes?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ2',
    type: 'single',
    stem: 'How often do you advise tobacco-using patients to quit?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ3',
    type: 'single',
    stem: 'How often do you assess a tobacco-using patient’s willingness (readiness) to make a quit attempt?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ4',
    type: 'single',
    stem: 'How often do you assist patients who want to quit — for example by providing self-help materials or discussing nicotine replacement therapy?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ5',
    type: 'single',
    stem: 'How often do you arrange follow-up or refer patients to a cessation service or quitline?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ6',
    type: 'single',
    stem: 'How often do you specifically ask patients about smokeless tobacco or areca (betel) nut use?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'),
  },
  {
    key: 'PQ7',
    type: 'single',
    stem: 'When you do NOT counsel a tobacco-using patient, what is the main reason?',
    options: same(
      'Lack of time',
      'Lack of training or confidence',
      'Belief it will not be effective',
      'Concern about offending the patient',
      'I always counsel tobacco-using patients',
    ),
  },
  {
    key: 'V1',
    type: 'single',
    stem: 'A patient who smokes says they are not ready to quit. The most appropriate approach is:',
    options: same(
      'Use the 5 R’s (motivational counselling) and offer help when they are ready',
      'Insist they stop immediately',
      'Say nothing, as it is their choice',
      'Refuse further dental treatment until they quit',
    ),
  },
  {
    key: 'V2',
    type: 'single',
    stem: 'A motivated patient asks for help to quit smoking. The most appropriate action is:',
    options: same(
      'Assist — offer or recommend pharmacotherapy (e.g. NRT) and arrange follow-up or a quitline referral',
      'Advise that willpower alone is sufficient',
      'Wish them luck with no further support',
      'Tell them to return only if they relapse',
    ),
  },
  {
    key: 'V3',
    type: 'single',
    stem: 'A young adult uses smokeless tobacco (gutkha) daily. The most appropriate action is:',
    options: same(
      'Advise cessation, examine the oral mucosa, and counsel or refer (smokeless tobacco is a major OPMD risk)',
      'Reassure that smokeless tobacco is a safe alternative to smoking',
      'Take no action as they are young',
      'Address it only if a lesion is already visible',
    ),
  },
  {
    key: 'IN1',
    type: 'likert',
    stem: 'When I begin (or continue) clinical practice, I intend to Ask and record every patient’s tobacco-use status.',
  },
  {
    key: 'IN2',
    type: 'likert',
    stem: 'I intend to Advise tobacco users to quit and to Assist or refer those who are ready to do so.',
  },
];

const education = [
  {
    id: 'tobacco-why',
    title: 'Why the dental chair matters',
    points: [
      'Tobacco — smoked and smokeless — is a leading cause of periodontal disease, implant failure, delayed wound healing, and oral potentially malignant disorders and oral cancer. In India, smokeless tobacco and areca (betel) nut are major drivers of oral cancer.',
      'Dentists see patients regularly and examine the mouth directly, so they are uniquely placed to spot tobacco harm early and to motivate quitting.',
      'Record every patient’s tobacco-use status — not only those with visible lesions. Treat it as a vital sign for oral health.',
    ],
    tip: 'Treat tobacco use as the 6th vital sign — check it at every visit, in every mouth.',
  },
  {
    id: 'tobacco-5as',
    title: 'The 5A’s — brief intervention that works',
    points: [
      'Ask about tobacco use and record it · Advise every user to quit, clearly and personally · Assess willingness to make a quit attempt · Assist with support and medication · Arrange follow-up or referral.',
      'Even brief advice — under three minutes — measurably increases the chance a patient quits. It is quick and it works.',
      'Use the Fagerström Test for Nicotine Dependence to gauge how dependent a patient is and tailor your support.',
    ],
    tip: '5 A’s, in order: Ask · Advise · Assess · Assist · Arrange. Even under three minutes of “Advise” moves the needle.',
  },
  {
    id: 'tobacco-5rs',
    title: 'Not ready to quit? Use the 5R’s',
    points: [
      'For patients who are unwilling to quit right now, build motivation with the 5R’s: Relevance, Risks, Rewards, Roadblocks, Repetition.',
      'Revisit at each visit — motivation changes over time, so repetition matters.',
    ],
    tip: '5 R’s for the not-ready: Relevance · Risks · Rewards · Roadblocks · Repetition.',
  },
  {
    id: 'tobacco-pharma',
    title: 'Medication and referral',
    points: [
      'Evidence-based pharmacotherapy includes nicotine-replacement therapy (patch, gum, lozenge), varenicline and bupropion.',
      'You don’t have to do it all yourself — refer to a telephone quitline or cessation service (in India, the National Tobacco Quitline and mCessation programme).',
    ],
    tip: 'Pills + phone beat willpower alone: NRT / varenicline / bupropion, plus a quitline referral.',
  },
];

export default {
  slug: 'tobacco',
  code: 'KS05',
  title: 'Effectiveness of a Brief Educational Intervention on Knowledge, Attitude, and Practice of Tobacco-Cessation Counselling Among Dental Students and Interns: A Pre–Post Interventional Study',
  shortTitle: 'Tobacco Cessation',
  studyTitle: 'KAP on Tobacco-Cessation Counselling',
  topic: 'Preventive dentistry & public health',
  emoji: '🚭',
  accent: '#34D399',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '2.0',
  practiceMax: 3, // scored practice = the three applied vignettes (V1–V3); PQ*/IN* are descriptive
  blurb: 'Knowledge of tobacco harms, attitudes to counselling, and use of the 5A’s brief-intervention in practice — with a built-in learning module that measures what changes.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'Helping patients quit: the essentials',
  educationBlurb: 'A two-minute refresher based on the US Public Health Service 5A’s/5R’s and WHO guidance. Read through, then continue to the short follow-up.',
};
